/*
 * The Article box: the change note, the two saves, and what the last one produced.
 *
 * WHERE IT SITS, AND WHAT THAT COSTS. It stacks under Publishing in the narrow
 * column, not under the text it describes. That is the arrangement asked for, and
 * it puts every act that changes what readers see in one column - but the buttons
 * are no longer beside the body they save, so on a long article they can be off
 * screen while the author is typing. The panel starts at the top of the page, so
 * they are never far.
 *
 * WHY IT IS A SIBLING OF PublishControls RATHER THAN PART OF IT. PublishControls
 * writes kb.Article's row; this one writes kb.ArticleRevision and never touches
 * kb.Article at all. Different table, different concurrency token - a publish is
 * guarded by the article's rowversion, a save by the base revision number - so
 * collapsing them would mean one form with two conflict stories.
 *
 * PRESENTATIONAL ON PURPOSE. It holds no state. The draft it edits and the save it
 * triggers belong to ArticleBody in ArticleEditorPage, because the same draft is
 * also being typed into the Text card in the other column; a box that owned half
 * of one revision would be the wrong seam.
 *
 * The release-note editor uses it too (S42, 2026-10-12) - same two saves, same
 * change note, on kb.ReleaseNoteRevision - with `heading="Release note"`. It reads
 * only the three fields of `latest` it prints, so either entity's latest revision
 * fits.
 *
 * Its words come from the chosen language's dictionary, `t.admin.articles` (S48);
 * the heading and placeholder defaults too, so a caller that passes neither gets
 * the article's in either language.
 */

import type { AdminRevisionSummary } from '@accotool-kb/shared';
import { DateTime, Field } from '../../components/ui.js';
import { useMessages } from '../../i18n/language.js';

export function ArticleSaveBox({
  changeNote,
  onChangeNote,
  onSave,
  saving,
  blocked,
  dirty,
  latest,
  heading,
  placeholder,
}: {
  changeNote: string;
  onChangeNote: (next: string) => void;
  /** true appends the revision and makes it the one readers get. */
  onSave: (publish: boolean) => void;
  saving: boolean;
  /** A title is missing, so neither save can run. */
  blocked: boolean;
  dirty: boolean;
  latest: Pick<AdminRevisionSummary, 'revisionNumber' | 'createdAtUtc' | 'createdBy'> | null;
  /** The card's heading: what is being saved. 'Article' unless told otherwise. */
  heading?: string;
  /** The change note's example, which should sound like the thing being edited. */
  placeholder?: string;
}): JSX.Element {
  const t = useMessages();
  const shared = t.admin.articles.shared;
  const words = t.admin.articles.saveBox;
  return (
    <div className="card">
      <div className="card-header">
        <h3>{heading ?? words.heading}</h3>
      </div>

      {/*
        No hint under the box: the placeholder already shows the shape of a change
        note, and this panel is used on every save, so a standing sentence about
        where the note ends up is read once and then in the way.
      */}
      <Field label={words.changeNote} htmlFor="change-note">
        <input
          id="change-note"
          type="text"
          value={changeNote}
          maxLength={500}
          placeholder={placeholder ?? words.placeholder}
          onChange={(event) => onChangeNote(event.target.value)}
        />
      </Field>

      <div className="button-row">
        <button
          type="button"
          className="button button-primary"
          onClick={() => onSave(true)}
          disabled={saving || blocked}
          title={words.saveAndPublishTitle}
        >
          {saving ? shared.saving : shared.saveAndPublish}
        </button>

        <button
          type="button"
          className="button"
          onClick={() => onSave(false)}
          disabled={saving || blocked}
          title={words.saveAsDraftTitle}
        >
          {words.saveAsDraft}
        </button>
      </div>

      {/*
        Below the buttons rather than inside the row with them. In this column the
        row is about 350px wide and an author identity is often a GUID, so left in
        the row it wrapped underneath anyway - as its own line it at least starts
        at the left edge. Measured at 43px, two lines, at every desktop width.
      */}
      {dirty && (
        <p className="unsaved" style={{ margin: 'var(--space-2) 0 0' }}>
          {shared.unsavedChanges}
        </p>
      )}
      {!dirty && latest && (
        <p className="faint" style={{ margin: 'var(--space-2) 0 0' }}>
          {t.article.revision(latest.revisionNumber)} · <DateTime value={latest.createdAtUtc} /> ·{' '}
          {latest.createdBy}
        </p>
      )}
    </div>
  );
}
